import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import Table from 'shared/components/Table';
import { useAuth } from 'shared/hooks/useAuth';
import { useTenantNavigate } from 'shared/hooks/useTenantNavigate';
import { canViewAllRecords } from 'shared/utils/canViewAllRecords';
import { tokens } from 'shared/theme';
import { listStatusParams, riskRegisterApi } from '../../api/systemRegisterApi';
import { apiErrorMessage, unwrapList } from '../../utils/responseHelpers';
import SoftwareRiskDialog, { emptySoftwareRiskRow } from '../../shared/SoftwareRiskDialog';
import {
  renderEnumChip,
  renderPriorityChip,
  renderRecordStatusChip,
  renderSoftwareRiskStatusChip,
} from '../../shared/statusChips';
import { sortRowsNewestFirst } from 'shared/utils/sortRowsNewestFirst';

const isRecordDeleted = (row) => String(row?.recordStatus || '').toUpperCase() === 'DELETED';

const cellText = (value, empty = '—') => {
  const text = value == null ? '' : String(value).trim();
  return text || empty;
};

const longTextColumn = (field, headerName, minWidth = 180) => ({
  field,
  headerName,
  flex: 1.1,
  minWidth,
  renderCell: ({ value }) => {
    const text = cellText(value);
    return (
      <Tooltip title={text === '—' ? '' : text} placement="top-start">
        <Typography
          variant="body2"
          noWrap
          sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {text}
        </Typography>
      </Tooltip>
    );
  },
});

/**
 * Standalone Risk Register — matches tenant-users table layout (shared Table + View all).
 */
export default function RiskRegisterPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { navigateTenant } = useTenantNavigate();
  const { user } = useAuth();
  const canViewAll = canViewAllRecords(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await riskRegisterApi.list(listStatusParams(canViewAll && showAll));
      const list = unwrapList(response);
      setRows(
        list.map((item) => ({
          id: item.id,
          riskCategory: item.riskCategory ?? '',
          resourcesImpacted: item.resourcesImpacted ?? '',
          riskName: item.riskName ?? '',
          riskPriority: item.riskPriority ?? '',
          riskDescription: item.riskDescription ?? '',
          riskOwner: item.riskOwner ?? '',
          impact: item.impact ?? '',
          causes: item.causes ?? '',
          controlsAndMitigation: item.controlsAndMitigation ?? '',
          status: item.status ?? 'OPEN',
          recordStatus: item.recordStatus ?? 'ACTIVE',
        }))
      );
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to load risk register.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canViewAll, showAll]);

  useEffect(() => {
    if (!canViewAll && showAll) {
      setShowAll(false);
    }
  }, [canViewAll, showAll]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(emptySoftwareRiskRow());
    setDialogOpen(true);
  };

  const openEdit = useCallback((row) => {
    if (isRecordDeleted(row)) return;
    setEditing({
      ...emptySoftwareRiskRow(),
      ...row,
      id: row.id,
      riskRegisterId: row.id,
    });
    setDialogOpen(true);
  }, []);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        riskCategory: values.riskCategory || undefined,
        resourcesImpacted: values.resourcesImpacted || undefined,
        riskName: values.riskName,
        riskPriority: values.riskPriority || undefined,
        riskDescription: values.riskDescription || undefined,
        riskOwner: values.riskOwner || undefined,
        impact: values.impact || undefined,
        causes: values.causes || undefined,
        controlsAndMitigation: values.controlsAndMitigation || undefined,
        status: values.status || 'OPEN',
      };
      if (values.id != null) {
        await riskRegisterApi.update(values.id, payload);
        toast.success('Risk updated');
      } else {
        await riskRegisterApi.create(payload);
        toast.success('Risk created');
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save risk.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget?.id) return;
    setRemoving(true);
    try {
      await riskRegisterApi.remove(removeTarget.id);
      toast.success('Risk marked as deleted');
      setRemoveTarget(null);
      await load();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete risk.'));
    } finally {
      setRemoving(false);
    }
  };

  const pagedRows = useMemo(() => {
    const ordered = sortRowsNewestFirst(rows);
    const start = page * size;
    return ordered.slice(start, start + size);
  }, [rows, page, size]);

  const columns = useMemo(
    () => [
      {
        field: 'riskCategory',
        headerName: 'Risk category',
        flex: 0.9,
        minWidth: 140,
        renderCell: renderEnumChip,
      },
      longTextColumn('resourcesImpacted', 'Resources impacted', 150),
      {
        field: 'riskName',
        headerName: 'Risk name',
        flex: 1.1,
        minWidth: 150,
      },
      {
        field: 'riskPriority',
        headerName: 'Risk priority',
        width: 130,
        renderCell: renderPriorityChip,
      },
      {
        field: 'riskOwner',
        headerName: 'Risk owner',
        flex: 1,
        minWidth: 140,
        valueFormatter: (params) => params?.value || '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        renderCell: renderSoftwareRiskStatusChip,
      },
      longTextColumn('riskDescription', 'Risk description', 180),
      longTextColumn('impact', 'Impact of the risk', 180),
      longTextColumn('causes', 'Causes of the risk', 180),
      longTextColumn('controlsAndMitigation', 'Controls and mitigation', 180),
      ...(showAll && canViewAll
        ? [
            {
              field: 'recordStatus',
              headerName: 'Record status',
              width: 130,
              renderCell: ({ row }) =>
                renderRecordStatusChip({
                  value: isRecordDeleted(row) ? 'DELETED' : row.recordStatus || 'ACTIVE',
                  row,
                }),
            },
          ]
        : []),
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const deleted = isRecordDeleted(row);
          return (
            <Box display="flex" alignItems="center" gap={0.25}>
              <Tooltip title="View risk">
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() =>
                      navigateTenant(`/system-register/risk-register/view?id=${row.id}`)
                    }
                    aria-label="View"
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Edit risk">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => openEdit(row)}
                    disabled={deleted}
                    aria-label="Edit"
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={deleted ? 'Already deleted' : 'Mark as deleted'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setRemoveTarget(row)}
                    disabled={deleted}
                    aria-label="Delete"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [canViewAll, navigateTenant, openEdit, showAll]
  );

  return (
    <Box m="20px">
      <Header title="Risk Register" subtitle="Record and track risks for IT governance" />

      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        {canViewAll ? (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            color="secondary"
            variant={showAll ? 'contained' : 'outlined'}
            size="small"
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
            onClick={() => {
              setShowAll(true);
              setPage(0);
            }}
          >
            View all
          </Button>
          <Button
            color="secondary"
            variant={!showAll ? 'contained' : 'outlined'}
            size="small"
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
            onClick={() => {
              setShowAll(false);
              setPage(0);
            }}
          >
            Active only
          </Button>
        </Box>
        ) : (
          <Box />
        )}

        <Button
          color="secondary"
          variant="contained"
          size="small"
          sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          onClick={openAdd}
          disabled={saving}
        >
          Add risk
        </Button>
      </Box>

      {loading ? (
        <LoadingSpinner message="Loading risk register…" height="300px" />
      ) : (
        <Table
          tableId="system-register.risk-register"
          title="Risk Register"
          rows={pagedRows}
          columns={columns}
          totalRecords={rows.length}
          size={size}
          page={page}
          handlePageChange={setPage}
          colors={colors}
        />
      )}

      <SoftwareRiskDialog
        open={dialogOpen}
        initialValues={editing}
        onClose={() => {
          if (saving) return;
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />

      <Dialog open={Boolean(removeTarget)} onClose={() => !removing && setRemoveTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete risk?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Do you want to delete the risk?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmRemove} disabled={removing}>
            {removing ? 'Deleting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
